import React from "react"

// firebase
import { Auth } from "services/firebase"
import { signInWithEmailAndPassword } from "firebase/auth"

// components
import { Button, Modal, Form, Input, message, Space, Flex } from "antd"
import { LoginOutlined } from "@ant-design/icons"

import { useFunnel } from "store/funnel"

import ResetPasswordModal from "./ResetPassword"
import Logo from "components/Logo"
import Typography from "antd/es/typography/Typography"

// i18n
import { useTranslation } from "react-i18next"

const LoginModal = ({ open, onCancel = () => null, onFinish = () => null }) => {
  const [loginForm] = Form.useForm()
  const [isLoading, setIsLoading] = React.useState(false)
  const [showReset, setShowReset] = React.useState(false)
  const [messageApi, contextHolder] = message.useMessage()
  const funnel = useFunnel()
  const { t } = useTranslation()

  return (
    <>
      {contextHolder}
      <ResetPasswordModal
        open={showReset}
        onCancel={() => setShowReset(false)}
      />

      <Modal
        data-testid="login-modal"
        title={t("login_title")}
        footer={
          true ? null : (
            <Flex justify="end" gap={8}>
              <Typography.Text type="secondary" style={{ fontStyle: "italic" }}>
                provided by
              </Typography.Text>
              <Logo />
            </Flex>
          )
        }
        open={open}
        onCancel={onCancel}
      >
        <Form
          form={loginForm}
          name="login"
          layout="vertical"
          onFinish={async (values) => {
            try {
              setIsLoading(true)

              await signInWithEmailAndPassword(
                Auth,
                values.email.trim(),
                values.password
              )
              funnel.add("login")
              onCancel()
              onFinish()
              loginForm.resetFields()
            } catch (e) {
              if (e.code === "auth/wrong-password") {
                messageApi.error(t("wrong_password"))
                return
              }

              if (e.code === "auth/invalid-email") {
                messageApi.error(t("invalid_email_auth"))
                return
              }

              if (e.code === "auth/user-not-found") {
                messageApi.error(t("account_not_found"))
                return
              }

              messageApi.error(e.message)
              console.error(e)
            } finally {
              setIsLoading(false)
            }
          }}
        >
          <Typography.Paragraph type="secondary">
            {t("login_info")}
          </Typography.Paragraph>

          <Form.Item
            label={t("email")}
            name="email"
            rules={[
              {
                required: true,
                message: t("please_enter_email"),
              },
            ]}
          >
            <Input autoComplete="email" />
          </Form.Item>

          <Form.Item
            label={t("password")}
            name="password"
            rules={[
              {
                required: true,
                message: t("please_enter_password"),
              },
            ]}
          >
            <Input.Password autoComplete="current-password" />
          </Form.Item>

          <Form.Item>
            <Space>
              <Button
                icon={<LoginOutlined />}
                type="primary"
                htmlType="submit"
                loading={isLoading}
              >
                {t("login")}
              </Button>

              <Button
                size="small"
                type="link"
                onClick={() => {
                  onCancel()
                  setShowReset(true)
                }}
              >
                {t("reset_password")}
              </Button>
            </Space>
          </Form.Item>
        </Form>
      </Modal>
    </>
  )
}

export default LoginModal
